import type { ReactNode } from 'react';
import {
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  cn,
} from '@mlm/ui';

/**
 * Small form-field helpers used across the structured ComPlan editors.
 * They keep label / input / hint spacing consistent without pulling in a
 * full form library — the editors are read/edit a JSON blob, not a server-
 * validated form, so React Hook Form would be overkill.
 */

export function FieldRow({
  label,
  hint,
  children,
  className,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
  className?: string;
}) {
  return (
    <div className={cn('space-y-1', className)}>
      <label className="text-xs font-medium text-foreground">{label}</label>
      {children}
      {hint ? <p className="text-[10px] text-muted-foreground">{hint}</p> : null}
    </div>
  );
}

export function NumberField({
  label,
  hint,
  value,
  onChange,
  disabled,
  min,
  max,
  step,
  placeholder,
  className,
}: {
  label: string;
  hint?: string;
  value: number | string | null | undefined;
  onChange: (v: number | null) => void;
  disabled?: boolean;
  min?: number;
  max?: number;
  step?: number;
  placeholder?: string;
  className?: string;
}) {
  return (
    <FieldRow label={label} hint={hint} className={className}>
      <Input
        type="number"
        className="h-9"
        disabled={disabled}
        value={value === null || value === undefined ? '' : String(value)}
        min={min}
        max={max}
        step={step ?? 0.01}
        placeholder={placeholder}
        onChange={(e) => {
          const raw = e.target.value;
          if (raw === '') return onChange(null);
          const n = Number(raw);
          if (Number.isFinite(n)) onChange(n);
        }}
      />
    </FieldRow>
  );
}

export function TextField({
  label,
  hint,
  value,
  onChange,
  disabled,
  placeholder,
  className,
}: {
  label: string;
  hint?: string;
  value: string | null | undefined;
  onChange: (v: string) => void;
  disabled?: boolean;
  placeholder?: string;
  className?: string;
}) {
  return (
    <FieldRow label={label} hint={hint} className={className}>
      <Input
        type="text"
        className="h-9"
        disabled={disabled}
        value={value ?? ''}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
      />
    </FieldRow>
  );
}

export function SelectField<T extends string>({
  label,
  hint,
  value,
  onChange,
  disabled,
  options,
  className,
}: {
  label: string;
  hint?: string;
  value: T;
  onChange: (v: T) => void;
  disabled?: boolean;
  options: Array<{ value: T; label: string }>;
  className?: string;
}) {
  return (
    <FieldRow label={label} hint={hint} className={className}>
      <Select value={value} onValueChange={(v) => onChange(v as T)} disabled={disabled}>
        <SelectTrigger className="h-9">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {options.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    </FieldRow>
  );
}

export function ToggleField({
  label,
  hint,
  value,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <label className="flex items-start gap-2 cursor-pointer select-none">
      <Checkbox
        checked={value}
        disabled={disabled}
        onCheckedChange={(checked) => onChange(checked === true)}
        className="mt-0.5"
      />
      <span className="space-y-0.5">
        <span className="text-xs font-medium block">{label}</span>
        {hint ? <span className="text-[10px] text-muted-foreground block">{hint}</span> : null}
      </span>
    </label>
  );
}
